export const normaliseTimeComponent = (raw: string | null | undefined, fallback: string): string => {
  if (typeof raw !== 'string') return fallback;
  const trimmed = raw.trim();
  if (!trimmed) return fallback;
  if (/^\d{2}:\d{2}$/.test(trimmed)) return `${trimmed}:00`;
  if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) return trimmed;
  return fallback;
};

export const composeEventDateTimeIso = (
  dateRaw: string | null | undefined,
  timeRaw: string | null | undefined,
  fallbackTime: string
): string | null => {
  if (typeof dateRaw !== 'string') return null;
  const trimmedDate = dateRaw.trim();
  if (!trimmedDate) return null;
  const normalizedDate = trimmedDate.slice(0, 10);
  if (!normalizedDate) return null;
  const normalizedTime = normaliseTimeComponent(timeRaw, fallbackTime);
  const isoCandidate = `${normalizedDate}T${normalizedTime}`;
  const parsed = new Date(isoCandidate);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

export const buildDateIsoWithFallback = (ymd: string, fallbackTime: string): string | null => {
  if (typeof ymd !== 'string') return null;
  const trimmed = ymd.trim().slice(0, 10);
  if (!trimmed) return null;
  const isoCandidate = `${trimmed}T${fallbackTime}`;
  const parsed = new Date(isoCandidate);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

export const normaliseYmd = (value?: string | null): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
  const [yearRaw, monthRaw, dayRaw] = normalized.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }
  return normalized;
};

/** Parse a calendar date without letting the JavaScript UTC date-only rule
 * move it to the previous day in western time zones. Noon also avoids the
 * uncommon daylight-saving transition that can occur around local midnight. */
export const dateOnlyToLocalDate = (value?: string | null): Date | null => {
  const normalized = normaliseYmd(value);
  if (!normalized) return null;
  const [year, month, day] = normalized.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

export const formatDateOnly = (
  value?: string | null,
  options?: Intl.DateTimeFormatOptions,
): string => dateOnlyToLocalDate(value)?.toLocaleDateString(undefined, options) ?? '';

/** "Sep 18", or "Sep 18, 2025" outside the current year, in the viewer's locale. A bare
 * YYYY-MM-DD stays on its calendar day; a timestamp shows the viewer's local day. */
export const formatShortDate = (value?: string | null, now: Date = new Date()): string => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) return '';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? dateOnlyToLocalDate(trimmed) : new Date(trimmed);
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
};

const toLocalDay = (value: string): Date | null => {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return dateOnlyToLocalDate(trimmed);
  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "Tue, Nov 10" for a bare date or a timestamp (the viewer's local day), with the year only
 * outside the current one. */
export const formatDayLabel = (value?: string | Date | null, now: Date = new Date()): string => {
  const date = value instanceof Date ? value : value ? toLocalDay(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
};

/** "Tue, Nov 10", or "Wed, Oct 28 – Fri, Oct 30" across days. A timestamp end at local midnight
 * belongs to the day before it. */
export const formatDayRange = (start?: string | null, end?: string | null, now: Date = new Date()): string => {
  const from = start ? toLocalDay(start) : null;
  let to = end ? toLocalDay(end) : null;
  if (from && to && end && !/^\d{4}-\d{2}-\d{2}$/.test(end.trim())
    && to.getHours() === 0 && to.getMinutes() === 0 && to.getSeconds() === 0 && to > from) {
    to = new Date(to.getTime() - 1);
  }
  const fromLabel = formatDayLabel(from, now);
  const toLabel = formatDayLabel(to, now);
  if (!fromLabel) return toLabel;
  if (!toLabel || toLabel === fromLabel) return fromLabel;
  return `${fromLabel} – ${toLabel}`;
};

export { isUsFederalHoliday, usFederalHolidayName } from './usFederalHolidays';
